import { useState } from 'react';
import { ArrowLeft, Copy, History } from 'lucide-react';
import {
  DashboardPageFormBody,
  DashboardPageFormShell,
  DashboardPageMetadataItem,
} from '@shigo-ui/dashboard-ui';
import { Button, Skeleton, toast } from '@shigo-ui/ui';

import { ReturnBackLink } from '../../../../../components/navigation/ReturnBackLink';
import { useReturnNavigation } from '../../../../../lib/returnNavigation';
import { showRequestError } from '../../../../../lib/requestErrorToast';
import { readShigoAppConfig } from '../../../../../lib/config';
import { ShigoDeploymentTarget } from '../../../../../lib/deployment';
import { dashboardT } from '../../../../../lib/messages';
import { useShigoSession } from '../../../../../lib/useShigoSession';
import { ChangelogDialog } from '../../../../changelog/components/ChangelogDialog';
import { NetworkAvailableFeature } from '../../../../auth/networkFeatures';
import { useNetworkFeatures } from '../../../../auth/useNetworkFeatures';
import { usePlacementMutations, usePlacementQuery } from '../../hooks';
import { PlacementType } from '../../model';
import { placementToFormValue, serializePlacementFormValue } from '../../placementFormModel';
import { emptyPlacementFormValue, type PlacementFormValue } from '../../placementFormSchema';
import { PlacementEditorForm } from './PlacementEditorForm';
import { copyToClipboard } from '../../../../../lib/clipboard';

export function PlacementEditorPage(props: {
  placementId?: string | undefined;
  placementType?: PlacementType | undefined;
}) {
  const { returnTo } = useReturnNavigation({ to: '/supply/placements' });
  const query = usePlacementQuery(props.placementId);
  const mutations = usePlacementMutations();
  const { isFeatureAllowed } = useNetworkFeatures();
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  const stitcherAllowed =
    isFeatureAllowed(NetworkAvailableFeature.Stitcher) &&
    (config.target !== ShigoDeploymentTarget.Production || session?.user.useStitcher === true);
  const [changelogOpen, setChangelogOpen] = useState(false);
  const editing = Boolean(props.placementId);
  const saving = mutations.create.isPending || mutations.update.isPending;
  const waitingForFreshDetail = editing && !query.isFetchedAfterMount;
  const goBack = () => void returnTo();

  async function copySecondaryId() {
    const secondaryId = query.data?.secondaryId;
    if (!secondaryId) return;
    await copyToClipboard(secondaryId, {
      successMessage: dashboardT('placements.copied'),
      errorMessage: dashboardT('placements.copyError'),
    });
  }

  async function save(value: PlacementFormValue) {
    try {
      if (props.placementId && query.data) {
        await mutations.update.mutateAsync({
          id: props.placementId,
          payload: serializePlacementFormValue(value),
        });
      } else {
        await mutations.create.mutateAsync(serializePlacementFormValue(value));
      }
    } catch (error) {
      showRequestError(error, dashboardT('placements.saveError'));
      return;
    }
    toast.success(dashboardT(editing ? 'placements.updatedSuccess' : 'placements.createdSuccess'));
    try {
      await returnTo({ replace: true });
    } catch {
      toast.error(dashboardT('placements.redirectError'));
    }
  }

  let content = null;
  if (waitingForFreshDetail && !query.isError) {
    content = (
      <div
        role="status"
        aria-label={dashboardT('placements.loadingDetails')}
        className="grid gap-4 p-6"
      >
        <Skeleton className="h-10 w-72 max-w-full" />
        <Skeleton className="h-80" />
      </div>
    );
  } else if (editing && query.isError) {
    content = (
      <DashboardPageFormBody title={dashboardT('placements.loadDetailsError')}>
        <Button type="button" variant="outline" onClick={() => void query.refetch()}>
          {dashboardT('placements.retry')}
        </Button>
      </DashboardPageFormBody>
    );
  } else if (!editing || query.data) {
    const placement = query.data;
    const placementType = placement?.type ?? props.placementType ?? PlacementType.Standard;
    content = (
      <PlacementEditorForm
        key={props.placementId ?? placementType}
        initialValue={
          placement ? placementToFormValue(placement) : emptyPlacementFormValue(placementType)
        }
        initialPublisher={placement?.publishers[0]}
        placementId={props.placementId}
        secondaryId={placement?.secondaryId}
        saving={saving}
        bidCacheAllowed={isFeatureAllowed(NetworkAvailableFeature.BidCache)}
        stitcherAllowed={stitcherAllowed}
        a2vAllowed={isFeatureAllowed(NetworkAvailableFeature.A2v)}
        adUnitAllowed={isFeatureAllowed(NetworkAvailableFeature.AdUnit)}
        onCancel={goBack}
        onSubmit={save}
      />
    );
  }

  return (
    <>
      <DashboardPageFormShell
        title={dashboardT(editing ? 'placements.editTitle' : 'placements.createTitle')}
        backAction={
          saving ? (
            <Button
              type="button"
              variant="outline"
              size="icon"
              disabled
              aria-label={dashboardT('placements.back')}
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
          ) : (
            <ReturnBackLink
              fallback={{ to: '/supply/placements' }}
              label={dashboardT('placements.back')}
            />
          )
        }
        secondaryRowActions={
          query.data ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setChangelogOpen(true);
              }}
            >
              <History className="h-4 w-4" aria-hidden="true" />
              {dashboardT('placements.changelog')}
            </Button>
          ) : undefined
        }
        secondaryRow={
          query.data ? (
            <>
              <DashboardPageMetadataItem
                label={dashboardT('placements.alternativeId')}
                value={
                  <span className="flex min-w-0 items-center gap-1">
                    <span className="truncate">{query.data.secondaryId ?? '-'}</span>
                    {query.data.secondaryId ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 shrink-0"
                        aria-label={dashboardT('placements.copyAlternativeId')}
                        onClick={() => void copySecondaryId()}
                      >
                        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
                      </Button>
                    ) : null}
                  </span>
                }
              />
              <DashboardPageMetadataItem
                label={dashboardT('placements.bidFloor')}
                value={query.data.bidFloor ?? '-'}
              />
            </>
          ) : undefined
        }
      >
        {content}
      </DashboardPageFormShell>
      {query.data && changelogOpen ? (
        <ChangelogDialog
          title={dashboardT('placements.changelogTitle', { name: query.data.name })}
          description={dashboardT('placements.changelogDescription')}
          scope={{ placementIds: [query.data.id] }}
          onClose={() => {
            setChangelogOpen(false);
          }}
        />
      ) : null}
    </>
  );
}
